import React, { useState, useMemo } from "react";
import {
  Popover,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import LayersIcon from "@mui/icons-material/Layers";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { Layer } from "../../models/maps/layer";

interface MapLayerControlProps {
  layers: Layer[];
  onToggleLayer: (layer: Layer) => void;
  onToggleGroup: (layers: Layer[], checked: boolean) => void;
}

const MapLayerControl: React.FC<MapLayerControlProps> = ({
  layers,
  onToggleLayer,
  onToggleGroup
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [search, setSearch] = useState("");

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const filteredLayers = useMemo(() => {
    if (!search.trim()) return layers;

    const lower = search.toLowerCase();

    return layers.filter(layer =>
      layer.layerDisplayName?.toLowerCase().includes(lower) ||
      layer.description?.toLowerCase().includes(lower) ||
      layer.sourceName?.toLowerCase().includes(lower)
    );
  }, [search, layers]);

  const wmsGroupedLayers = useMemo(() => {
    const groups: Record<string, Layer[]> = {};

    filteredLayers.forEach(layer => {
      if (layer.type === 'geopackage') {
        return;
      }

      const key = layer.sourceName || "Overig";

      if (!groups[key]) groups[key] = [];
      groups[key].push(layer);
    });

    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) =>
        a.layerDisplayName.localeCompare(b.layerDisplayName)
      );
    });

    return Object.entries(groups).sort(([a], [b]) =>
      a.localeCompare(b)
    );
  }, [filteredLayers]);

  const assetGroupedLayers = useMemo(() => {
    const groups: Record<string, Layer[]> = {};

    filteredLayers.forEach(layer => {
      if (layer.type !== 'geopackage') {
        return;
      }

      const key = layer.sourceName || "Overig";

      if (!groups[key]) groups[key] = [];
      groups[key].push(layer);
    });

    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) =>
        a.layerDisplayName.localeCompare(b.layerDisplayName)
      );
    });

    return Object.entries(groups).sort(([a], [b]) =>
      a.localeCompare(b)
    );
  }, [filteredLayers]);

  const renderSourceAccordion = (sourceName: string, groupedSourceLayers: Layer[], sectionKey: string) => {
    const allSelected = groupedSourceLayers.every(l => l.selected);
    const someSelected =
      groupedSourceLayers.some(l => l.selected) && !allSelected;

    return (
      <Accordion
        key={`${sectionKey}-${sourceName}`}
        disableGutters
        sx={{
          boxShadow: "none",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          "&:before": { display: "none" }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: "32px",
            paddingLeft: "2px",
            "& .MuiAccordionSummary-content": {
              margin: "4px 0"
            }
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginLeft: 0,
            }}>
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) =>
                onToggleGroup(groupedSourceLayers, e.target.checked)
              }
              size="small"
            />

            <Typography fontWeight={600}>
              {sourceName}
            </Typography>
          </Box>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            pt: 0,
            pb: 0.5
          }}
        >
          {groupedSourceLayers.map(layer => (
            <Box
              key={layer.id}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                pl: 0
              }}
            >
              <FormControlLabel
                sx={{
                  marginLeft: 0,
                  marginRight: 0,
                }}
                control={
                  <Checkbox
                    checked={layer.selected ?? false}
                    onChange={() => onToggleLayer(layer)}
                    size="small"
                  />
                }
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1
                    }}
                  >
                    <span>{layer.layerDisplayName}</span>
                    {/* {layer.type === "asset" && typeof layer.assetCount === "number" && (
                      <Typography variant="caption" color="text.secondary">
                        ({layer.assetCount})
                      </Typography>
                    )} */}
                    {/* <span
                      className={`type-badge type-badge-${layer.type}`}
                    >
                      {layer.type.toUpperCase()}
                    </span> */}
                  </Box>
                }
              />

              {layer.description && (
                <Tooltip
                  title={layer.description}
                  placement="right"
                  arrow
                >
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <>
      {!!layers.length && (
        <button
          type="button"
          className="azure-map-button azure-map-button-top-left-second"
          onClick={handleClick}
        >
          <LayersIcon fontSize="small" />
        </button>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              border: "1px solid rgba(0,0,0,0.15)"
            }
          }
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          Kaartlagen
        </Typography>

        <TextField
          fullWidth
          variant="filled"
          size="small"
          hiddenLabel
          placeholder="Zoek kaartlaag..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            mb: 1,
            "& .MuiFilledInput-root": {
              borderRadius: "4px",
              backgroundColor: "#f5f5f5"
            }
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="wis zoekfilter"
                    onClick={() => setSearch("")}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined
            }
          }}
        />

        <Typography variant="caption" color="text.secondary">
          {filteredLayers.length === 1
            ? "1 kaartlaag"
            : `${filteredLayers.length} kaartlagen`}
        </Typography>

        <Divider sx={{ mb: 1 }} />

        <Box sx={{ maxHeight: 500, overflowY: "auto" }}>
          {!!wmsGroupedLayers.length && (
            <Box sx={{ mb: assetGroupedLayers.length ? 1 : 0 }}>
              <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>
                Algemeen
              </Typography>
              {wmsGroupedLayers.map(([sourceName, sourceLayers]) =>
                renderSourceAccordion(sourceName, sourceLayers, "wms")
              )}
            </Box>
          )}

          {!!assetGroupedLayers.length && (
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>
                Organisaties
              </Typography>
              {assetGroupedLayers.map(([sourceName, sourceLayers]) =>
                renderSourceAccordion(sourceName, sourceLayers, "asset")
              )}
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default MapLayerControl;